/* eslint-disable function-paren-newline */
import React, { useCallback, useEffect, useState } from 'react';
import { FormState, FormApi } from 'final-form';
import { FormSpy } from 'react-final-form';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { IAccount } from 'store/accounts.model';
import { getFileSize } from 'utils/formatters';
import noop from 'utils/noop';
import { AxiosStatus } from 'api/axiosConfig';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import Form from 'ui/Form/Form';
import { requiredOnly, validationCallback } from 'utils/validation';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { NETWORK_REQUIRED } from 'utils/constants';

import Table from 'ui/Table/Table';
import Button, { Themes as ButtonThemes, Sizes as ButtonSizes } from 'ui/Button/Button';
import { ITransactionField } from 'ui/structure/TransactionCard';
// import { DEFAULT_ACCOUNT_TYPE } from 'utils/constants';
// import validatePin from 'utils/pinValidation';
// import { IApiResponse } from 'utils/models';
import TablePagination from 'ui/TableInputs/TablePagination/TablePagination';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import { IApiResponse } from 'utils/models';
import { OTPConfirmationModal, StatusModal, WarningModal } from 'components/modals';
import routePaths from 'routes/routePaths';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import api from 'api/api';
import { IOTPError } from 'components/authModules/LogIn/login.model';

import {
  IFormValues,
  IExtraFields,
  FAB_BANK,
  FormType,
  bulkDataConfig,
  bulkSortArray,
  IBulkUploadData,
  IBulkDataList,
} from './FileUpload.model';
import { renderTransferFieldsFrom, renderBulkTransferUpload } from './FileUploadFields';
// import { bulkSubmit } from './submitApi';
import columns from './columns';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './FileUpload.module.css';

import trash from 'assets/icons/trash.png';
import excel from 'assets/icons/excel.png';
import download from 'assets/img/download.png';

interface IProps {
  accounts: IAccount[];
  bulkUploadState: 'adding' | 'confirmation' | 'report';
  // eslint-disable-next-line react/no-unused-prop-types
  openAddBulkUploadForm: () => void;
  showMainForm: boolean;
  handleChangeForm: () => void;
}

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  files: requiredOnly('File upload'),
});

const FileUpload = ({ accounts, bulkUploadState, showMainForm, handleChangeForm }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const emptyMessage = 'Pending payments will be displayed here.';
  const [simpleWarning, setSimpleWarning] = useState<string>();
  const [disableBulkTransfer, setDisableMakeTransfer] = useState<boolean>(false);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  const [tableData, setTableData] = useState<IBulkDataList[]>([]);
  const [mainData, setMainData] = useState<IBulkDataList[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  // Extras
  const INITIAL_EXTRAS: IExtraFields = {
    destinationBankName: '',
    destinationBankCode: '',
    destinationAccountName: '',
    destinationIsExistingBen: false,
  };
  const [extras, setExtras] = useState<IExtraFields>(INITIAL_EXTRAS);
  const updateExtras = useCallback(
    (update: Partial<IExtraFields>) => setExtras((current) => ({ ...current, ...current, ...update })),
    [],
  );

  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

  const handleDeleteFile = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, form: FormType) => {
    e.stopPropagation();
    form.change('files', []);
    mainForm.change('files', []);
    setTableData([]);
  };

  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);
  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };
  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const getPrincipalAmount = () => {
    const transferList = mainData;

    if (transferList.length > 0) {
      const result = transferList.map((item) => Number(item.amount)).reduce((a, b) => a + b, 0);
      return result;
    }
    return 0;
  };

  const onChangeForm = useCallback(
    ({ values, dirty }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }
      const { destinationAccountNo } = values || {};
      const destAccount = accounts.find((a) => a.accountNumber === destinationAccountNo);
      if (destAccount) {
        updateExtras({
          destinationAccountName: destAccount.accountName,
          destinationBankName: FAB_BANK.name,
          destinationBankCode: FAB_BANK.code,
          destinationIsExistingBen: false,
          beneficiaryId: undefined,
        });
      } else {
        console.error('DID NOT FIND OWN ACCOUNT');
        // todo: if destAccount not found, prompt
      }
    },
    [accounts, updateExtras, handleChangeForm],
  );

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const BulkConfirmation = () => {
    const { sourceAccount } = mainForm.getState().values;
    const readyValues = {
      sourceAccount,
      numberOfTransactions: mainData.length,
      totalAmount: getPrincipalAmount(),
    };

    const account = accounts.find((a) => a.accountNumber === sourceAccount);

    if (Number(account?.availableBalance) < Number(readyValues.totalAmount)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg(`Transfer failed.
      Insufficient funds to complete the transaction.`);
      return;
    }

    setPreviewData(getTransactionsArray(readyValues, bulkDataConfig, undefined, bulkSortArray));
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  };

  const onResend = () => api.post('/customer/otp/generate');
  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const handleAddBulk = useCallback(
    (values: IFormValues) => {
      setSubmitStatus(AxiosStatus.PENDING);
      const { sourceAccount } = values;
      const fromAccount = accounts.find((a) => a.accountNumber === sourceAccount);
      if (!fromAccount) {
        console.error('Invalid source account selected');
        // TODO: show prompt
      }
      const requestBody = {
        sourceAccount: fromAccount?.accountNumber,
        file: values.files[0],
      };
      api
        .postWithFiles('transfer/bulkUploadPreparedCard', requestBody, true, false)
        .then((data: IBulkUploadData) => {
          setMainData(data.bulkUploadData);
          if (data.bulkUploadData?.length > 0) {
            setTableData(data.bulkUploadData);
            setTotalRows(data.bulkUploadData.length);
            const allValid = data.bulkUploadData.some((item) => item.code === 10);
            if (allValid) {
              setDisableMakeTransfer(true);
            } else {
              setDisableMakeTransfer(false);
            }
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setSubmitStatus(AxiosStatus.DEFAULT);
        });
    },
    [accounts, extras],
  );

  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      const translist = mainData.map((item) => ({
        amount: item.amount,
        cardLastDigit: item.cardLastDigit,
        narration: item.narration,
        customerId: item.customerId,
        phoneNumber: item.phoneNumber,
        beneficiaryName: item.beneficiaryName,
        // cardName: item.cardName,
        transactionType: 'PREPAIDCARD-TRANSFER',
      }));

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);

          const requestBody = {
            sourceAccount: values.sourceAccount,
            transferData: JSON.stringify(translist),
            password: otp,
            passwordType: 'OTP',
            saveBen: saveBeneficiary,
          };

          api
            .post('transfer/manualCardPrepared ', requestBody, true, true)
            .then((response: IApiResponse) => {
              setSuccessMsg(response.description || 'Request Completed Successfully!');
              closeConfirmation();
              setSubmitStatus(AxiosStatus.SUCCESS);
            })
            .catch((error: IApiResponse) => {
              console.error(error);
              setErrorMsg(error.description || 'Request is failed. Unable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
              closeConfirmation();
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [accounts, mainData],
  );

  useEffect(() => {
    const data = mainData;
    const indexOfLastPost = pageNumber * pageSize;
    const indexOfFirstPost = indexOfLastPost - pageSize;
    const currentData = data.slice(indexOfFirstPost, indexOfLastPost);
    setTableData(currentData);
  }, [mainData, pageNumber, pageSize]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  const handleCancelBulk = () => {
    setTableData([]);
    mainForm.change('files', []);
  };

  const subTitleItem = (
    <div>
      <p className={styles.subTitleItem}>
        Download the template below, complete it and upload to the section on the right
      </p>
      <a className={styles.downloadText} href="/excel_templates/Prepaid bulk template.xlsx" target="_parent" download>
        <Button type="button" className={styles.btnSubmit} theme={ButtonThemes.secondary} size={ButtonSizes.small}>
          <img src={download} alt="delete icon" />
          Download Template
        </Button>
      </a>
    </div>
  );

  const renderTableData = () => (
    <>
      <Table columns={columns} data={tableData} emptyTableMessage={emptyMessage} />
      <TablePagination
        count={Math.ceil(totalRows / pageSize)}
        page={pageNumber}
        pageSize={pageSize}
        totalCount={totalRows}
        handlePageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
      />
    </>
  );

  const renderBulkUploadField = (values: IFormValues, form: FormType) => {
    if (Object.keys(values).length !== 0) {
      if (values.files && values.files.length > 0) {
        return (
          <div className={styles.valueContentContainer}>
            <div className={styles.nameAndSizeContainer}>
              <img src={excel} alt="excel icon" />
              <div>
                <p className={styles.name}>{values.files[0] && values.files[0].name}</p>
                <p className={styles.size}>{values.files[0] && getFileSize(values.files[0].size)}</p>
              </div>
            </div>
            <div
              className={styles.fileDeleteBtn}
              onClick={(e) => handleDeleteFile(e, form)}
              role="button"
              tabIndex={0}
              onKeyPress={noop}
            >
              <img src={trash} alt="delete icon" width="40" height="40" />
            </div>
          </div>
        );
      }
    }
    return renderBulkTransferUpload(submitting);
  };

  return (
    <>
      {showMainForm && (
        <DashboardCard>
          <Form
            onSubmit={handleAddBulk}
            validate={validate}
            mutators={{ changeFieldValue, setFieldTouched }}
            render={({ handleSubmit, values, form, hasValidationErrors }) => {
              mainForm = mainForm || form;
              return (
                <form onSubmit={handleSubmit}>
                  <FieldsLeveler label="Source Account">{renderTransferFieldsFrom(accounts, submitting)}</FieldsLeveler>
                  <FieldsLeveler label="Beneficiary List" subTitle={subTitleItem}>
                    {renderBulkUploadField(values, form)}
                  </FieldsLeveler>
                  <SubmitLeveler>
                    <Button type="submit" disabled={hasValidationErrors || submitting} fullWidth>
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Upload Form'}
                    </Button>
                    <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
                      {simpleWarning}
                    </WarningModal>
                  </SubmitLeveler>
                  <OTPConfirmationModal
                    open={isConfirmationOpen}
                    data={previewData}
                    onClose={closeConfirmation}
                    onResend={onResend}
                    OTPPinError={OTPError}
                    onSubmit={onConfirm(values)}
                    submitStatus={submitStatus}
                    onSuccess={handleOnSuccess}
                    onRetry={onRetryConfirm}
                    successText={successMsg}
                    failureText={errorMsg}
                    askSaveBeneficiary
                  />
                  <StatusModal
                    open={showStatusModal}
                    status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                    onClose={onCloseStatusModal}
                    onRetry={onCloseStatusModal}
                    onRetryText="Close"
                  >
                    {submitStatus === AxiosStatus.SUCCESS ? (
                      successMsg
                    ) : (
                      <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
                    )}
                  </StatusModal>
                  <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
                </form>
              );
            }}
          />
        </DashboardCard>
      )}
      <DashboardCard>
        {bulkUploadState === 'adding' && tableData.length > 0 && (
          <>
            <NavigationTitle to="#/" layoutType={LayoutType.dashboard} noArrow>
              Bulk List
            </NavigationTitle>
            {renderTableData()}
            <SubmitLeveler>
              <Button
                type="button"
                fullWidth
                disabled={!tableData.length || disableBulkTransfer}
                onClick={BulkConfirmation}
              >
                Proceed
              </Button>
            </SubmitLeveler>
            <SubmitLeveler>
              <div
                role="button"
                className={styles.cancelTransfer}
                onClick={handleCancelBulk}
                tabIndex={0}
                onKeyPress={noop}
              >
                Cancel
              </div>
            </SubmitLeveler>
          </>
        )}
      </DashboardCard>
    </>
  );
};

export default FileUpload;
